import { useAtom } from 'jotai/react'
import { useEffect, useMemo } from 'react'

import { ENV_PARAMS } from '@/constants/evnParams'
import { EnableTrade } from '@/constants/trade'
import { METHOD } from '@/lib/fetcher'
import { fromChainIdAtom, toChainIdAtom } from '@/stores/trade'

import { useFetch, usePages } from './useFetch'

export type BaseTokenInfo = {
  exp_amount_list: Array<{
    // 到账时效
    exp_amount_rate: number // 到账比例百分比
    time: number //单位分钟
  }>
  fee: string // 百分比手续费
  fixed_fee: string // 固定手续费
  fromtokencadd: string
  fromtokendecimal: number
  fromtokenlogourl: string
  fromtokensymbol: string
  max_num: string // 转账最大
  min_num: string // 转账最小
  receive_addr_status: 1 | 0 // 1 可以填写自定义地址 0不能填写自定义地址 这里是收款地址
  status: 1 | 0 // 1= token 可以交易，0 =token 不可以交易
  totokencadd: string
  totokendecimal: number
  totokenlogourl: string
  totokensymbol: string
  zhubi_fee: string // 主币手续费
  zhubi_bf_fee: string // 主币百分比手续费
}

export type TokenMeta = {
  address: string
  decimal: number
  symbol: string
  logo: string
}

export type TokenInfo = {
  fromToken: TokenMeta
  toToken: TokenMeta
  expAmountList: Array<{ expAmountRate: number; time: number }>
  fee: BaseTokenInfo['fee']
  fixedFee: BaseTokenInfo['fixed_fee']
  nativeFee: BaseTokenInfo['zhubi_fee']
  nativeFeePrecent: BaseTokenInfo['zhubi_bf_fee']
  enableTrade: EnableTrade
  enableReceiveAddress: EnableTrade
  limit: {
    max: BaseTokenInfo['max_num']
    min: BaseTokenInfo['min_num']
  }
}

export const useTokenInfo = () => {
  const [fromChainId] = useAtom(fromChainIdAtom)
  const [toChainId] = useAtom(toChainIdAtom)

  const { page, setTotalPage } = usePages(1)

  const { data } = useFetch<{
    data: BaseTokenInfo[]
    fromchainid: string
    tochainid: string
    p: string
    totalpage: number
  }>({
    url: fromChainId && toChainId ? (ENV_PARAMS.MODE === 'prod' ? '/tokenInfo' : '/testtokeninfo') : null,
    method: METHOD.GET,
    data: {
      from_chain_id: fromChainId,
      to_chain_id: toChainId,
      p: page
    }
  })

  const [tokenInfos, totalPage] = useMemo(() => {
    if (!data || data.code !== 200 || !data.data) return []
    const { data: baseTokenInfos, totalpage } = data.data
    return [
      baseTokenInfos.map<TokenInfo>((baseTokenInfo) => ({
        fromToken: {
          address: baseTokenInfo.fromtokencadd,
          decimal: baseTokenInfo.fromtokendecimal,
          symbol: baseTokenInfo.fromtokensymbol,
          logo: baseTokenInfo.fromtokenlogourl
        },
        toToken: {
          address: baseTokenInfo.totokencadd,
          decimal: baseTokenInfo.totokendecimal,
          symbol: baseTokenInfo.totokensymbol,
          logo: baseTokenInfo.totokenlogourl
        },
        limit: {
          max: baseTokenInfo.max_num,
          min: baseTokenInfo.min_num
        },
        expAmountList: baseTokenInfo.exp_amount_list.map(({ exp_amount_rate, time }) => ({
          expAmountRate: exp_amount_rate,
          time
        })),
        enableReceiveAddress: baseTokenInfo.receive_addr_status === 1 ? EnableTrade.Enabled : EnableTrade.Disabled,
        enableTrade: baseTokenInfo.status === 1 ? EnableTrade.Enabled : EnableTrade.Disabled,
        fee: baseTokenInfo.fee,
        fixedFee: baseTokenInfo.fixed_fee,
        nativeFee: baseTokenInfo.zhubi_fee,
        nativeFeePrecent: baseTokenInfo.zhubi_bf_fee || '0'
        // nativeFeePrecent: '0.0001'
      })),
      totalpage
    ]
  }, [data])

  useEffect(() => {
    setTotalPage(totalPage)
  }, [setTotalPage, totalPage])

  return {
    tokenInfos
  }
}
